import React, { useState, useEffect } from "react";
import { Bell, Building, Activity } from "lucide-react";
import { fetchNotice, STORAGE_KEYS } from "../services/api";

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    loadNotices();
    loadUserType();
  }, []);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadNotices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the organization ID from localStorage
      const orgId = localStorage.getItem("last_selected_org_id") || 0;
      // console.log("Using organization ID for notices:", orgId);

      // Fetch notices using the stored organization ID
      const notices = await fetchNotice(orgId);
      // console.log("Fetched notices:", notices);

      // Use the same mapping technique as dashboard - direct mapping
      const mappedNotices = notices.map((notice, index) => ({
        id: notice.id || index + 1,
        name: notice.name || notice.Name || "Untitled Notice",
        description:
          notice.description ||
          notice.Description ||
          "No description available",
        timestamp:
          notice.Created ||
          notice.created ||
          notice.DateCreated ||
          new Date().toISOString(),
      }));

      setNotifications(mappedNotices);
    } catch (err) {
      console.error("Failed to load notices:", err);
      setError(err.message || "Failed to load notices");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {" "}
      {/* Responsive vertical spacing */}
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
          {userType.isHospital ? (
            <Bell className="h-6 w-6 text-red-600 mr-2" />
          ) : userType.isBDO ? (
            <Bell className="h-6 w-6 text-green-600 mr-2" />
          ) : null}
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
            Notices
          </h1>
        </div>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">
          Stay updated with the latest notices and announcements
        </p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadNotices}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}
      {notifications.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-4 md:p-8 text-center">
          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No notices</h3>
          <p className="text-gray-600 text-sm sm:text-base">
            No notices available at the moment. Check back later for updates.
          </p>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-4">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="card p-2 sm:p-4 hover:shadow-medium transition-shadow duration-200"
            >
              <div className="flex items-start">
                <div className="p-2 rounded-lg bg-primary-100">
                  <Bell className="h-5 w-5 text-primary-600" />
                </div>
                <div className="ml-2 sm:ml-4 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {notification.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {notification.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
